import type { ICellEditorComp, ICellEditorParams } from "ag-grid-enterprise";

interface LargeTextEditorParams {
  maxLength?: number;
}

export class InlineLargeTextCellEditor implements ICellEditorComp {
  private params!: ICellEditorParams;
  private textarea!: HTMLTextAreaElement;

  private baselineRowHeight = 0;
  private cellVerticalPadding = 0;
  private lastRowHeight: number | null = null;

  private widthObserver: ResizeObserver | null = null;
  private lastWidth = -1;
  private rowGrowRaf = 0;

  private onInput = (): void => {
    this.autosize();
    this.scheduleRowGrow();
  };

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.stopPropagation();
    }
  };

  init(params: ICellEditorParams): void {
    this.params = params;
    const { maxLength } = params as ICellEditorParams & LargeTextEditorParams;

    const textarea = document.createElement("textarea");
    textarea.rows = 1;
    if (maxLength != null) textarea.maxLength = maxLength;
    textarea.value = params.value == null ? "" : String(params.value);

    const style = textarea.style;
    style.font = "inherit";
    style.color = "inherit";
    style.lineHeight = "var(--clio-ui-data-grid-cell-line-height)";
    style.boxSizing = "border-box";
    style.display = "block";
    style.width = "100%";
    style.margin = "0";
    style.padding = "0";
    style.paddingLeft = "var(--ag-cell-horizontal-padding)";
    style.border = "0";
    style.background = "transparent";
    style.outline = "none";
    style.resize = "none";
    style.overflowY = "auto";
    style.maxHeight = "50vh";
    style.alignSelf = "flex-start";

    textarea.addEventListener("input", this.onInput);
    textarea.addEventListener("keydown", this.onKeydown);

    this.textarea = textarea;
  }

  getGui(): HTMLElement {
    return this.textarea;
  }

  getValue(): string {
    return this.textarea.value;
  }

  isPopup(): boolean {
    return false;
  }

  afterGuiAttached(): void {
    const cellStyle = this.params.eGridCell
      ? getComputedStyle(this.params.eGridCell)
      : null;
    this.cellVerticalPadding = cellStyle
      ? parseFloat(cellStyle.paddingTop) + parseFloat(cellStyle.paddingBottom)
      : 0;
    this.baselineRowHeight = this.params.node?.rowHeight ?? 0;

    this.textarea.focus();
    const end = this.textarea.value.length;
    this.textarea.setSelectionRange(end, end);

    this.autosize();
    this.scheduleRowGrow();

    if (typeof ResizeObserver !== "undefined") {
      this.widthObserver = new ResizeObserver(() => {
        const width = this.textarea.clientWidth;
        if (width !== this.lastWidth) {
          this.lastWidth = width;
          this.autosize();
          this.scheduleRowGrow();
        }
      });
      this.widthObserver.observe(this.textarea);
    }
  }

  destroy(): void {
    this.textarea.removeEventListener("input", this.onInput);
    this.textarea.removeEventListener("keydown", this.onKeydown);
    this.widthObserver?.disconnect();
    this.widthObserver = null;
    if (this.rowGrowRaf) {
      cancelAnimationFrame(this.rowGrowRaf);
      this.rowGrowRaf = 0;
    }
  }

  private autosize(): void {
    this.textarea.style.height = "auto";
    this.textarea.style.height = `${this.textarea.scrollHeight}px`;
  }

  private scheduleRowGrow(): void {
    if (typeof requestAnimationFrame === "undefined") {
      this.growRow();
      return;
    }
    if (this.rowGrowRaf) cancelAnimationFrame(this.rowGrowRaf);
    this.rowGrowRaf = requestAnimationFrame(() => {
      this.rowGrowRaf = 0;
      this.growRow();
    });
  }

  private growRow(): void {
    const node = this.params.node;
    if (!node) return;

    const target = Math.max(
      this.textarea.offsetHeight + this.cellVerticalPadding,
      this.baselineRowHeight
    );
    if (
      this.lastRowHeight !== null &&
      Math.abs(target - this.lastRowHeight) < 1
    ) {
      return;
    }
    this.lastRowHeight = target;
    node.setRowHeight(target);
    this.params.api.onRowHeightChanged();
  }
}
